import type {
  AcceptedSolutionMessage,
  Scalars
} from '@aurora/shared-generated/types/graphql-schema-types';
import { EntityType, VisibilityScope } from '@aurora/shared-generated/types/graphql-schema-types';
import type { Tenant } from '@aurora/shared-types/tenant';
import IdConverter from '@aurora/shared-utils/graphql/IdConverter/IdConverter';
import placeholderAttachmentConnection from './placeholderAttachmentConnection';
import placeholderMessageConnection from './placeholderMessageConnection';
import placeholderRatingConnection from './placeholderRatingConnection';
import placeholderRevisionConnection from './placeholderRevisionConnection';
import placeholderRevisionsDiffResponse from './placeholderRevisionsDiffResponse';

function getAcceptedSolutionMessageMessageRequiredFields(
  tenant: Tenant,
  id: Scalars['ID']['output']
): AcceptedSolutionMessage {
  return {
    entityType: EntityType.ForumReply,
    eventPath: id,
    author: null,
    attachments: null,
    board: null,
    conversation: null,
    currentRevision: null,
    ancestors: placeholderMessageConnection(),
    descendants: placeholderMessageConnection(),
    depth: 0,
    hasGivenKudo: false,
    kudosSumWeight: 0,
    id,
    subscribed: false,
    language: null,
    messagePolicies: null,
    metrics: null,
    parent: null,
    postTime: null,
    postTimeFriendly: null,
    readOnly: false,
    replies: placeholderMessageConnection(),
    revisionNum: 0,
    solution: false,
    subject: null,
    timeToRead: 0,
    uid: Number.parseInt(IdConverter.idToTuple(id).id, 10),
    repliesCount: 0,
    revisions: placeholderRevisionConnection(),
    visibilityScope: VisibilityScope.Public,
    editFrozen: false,
    body: null,
    rawBody: null,
    read: false,
    plusOnes: null,
    ratings: placeholderRatingConnection(),
    customFields: [],
    customFormFieldsForUpdate: [],
    hasCustomFormFieldsSet: false,
    viewHref: '/'
  };
}

function getDefaultAcceptedSolutionMessageMessage(
  tenant: Tenant,
  id: Scalars['ID']['output']
): Required<AcceptedSolutionMessage> {
  return {
    ...getAcceptedSolutionMessageMessageRequiredFields(tenant, id),
    __typename: 'AcceptedSolutionMessage',
    attachments: placeholderAttachmentConnection(),
    body: null,
    images: null,
    kudos: undefined,
    kudosSumWeight: 0,
    plusOne: false,
    parent: null,
    placeholder: false,
    editFrozen: false,
    read: false,
    searchSnippet: null,
    solutionData: null,
    rawBody: null,
    replyType: null,
    metrics: null,
    lastPublishTime: null,
    revisionNum: 0,
    originalMessageForPlaceholder: null,
    moderationData: null,
    abuseReports: null,
    visibilityScope: VisibilityScope.Public,
    latestVersion: null,
    latestRevision: null,
    revisionsDiff: placeholderRevisionsDiffResponse(),
    videos: null,
    customField: () => null,
    isEscalated: false,
    showMoveIndicator: false,
    viewHref: '/'
  };
}

export default function placeholderAcceptedSolutionMessage(
  tenant: Tenant,
  message: Partial<AcceptedSolutionMessage> = {},
  useDefaults = true
): AcceptedSolutionMessage {
  let id;
  if (message?.id) {
    id = message?.id;
  } else {
    const uid = message?.uid ?? `${Date.now()}|temp`;
    id = `message:${uid}`;
  }
  return {
    id,
    ...(useDefaults
      ? getDefaultAcceptedSolutionMessageMessage(tenant, id)
      : getAcceptedSolutionMessageMessageRequiredFields(tenant, id)),
    ...message
  };
}
